/**
 * PDP delivery estimates: server-side orchestration (no browser SCAPI basket mutation).
 *
 * Calls `/server/shipping/pdp-sfcc-estimate` which:
 *   1. Creates an isolated guest SCAPI basket on the MRT server (not the customer's).
 *   2. Adds the decomposed product SKUs, sets the shipping address.
 *   3. Retrieves SFCC `applicableShippingMethods`.
 *   4. Deletes the temporary basket.
 *   5. Feeds the SFCC methods into the same shipping orchestration as checkout
 *      (external API merge, lead-time enrichment, priority filter).
 *
 * The hook is a thin `useQuery` wrapper — all heavy lifting happens server-side.
 *
 * @see server/services/shipping/pdp-sfcc-shipping.js
 * @see server/routes/shipping.js (POST /pdp-sfcc-estimate)
 */

import {useMemo} from 'react';
import {useQuery} from '@tanstack/react-query';

import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';

/**
 * @param {Object} method
 * @returns {number}
 */
function methodPriceAmount(method) {
  const p = method?.price;

  if (p && typeof p === 'object' && p.amount != null) {
    return Number(p.amount) || 0;
  }

  const n = method?.actualPrice ?? p;

  return n == null ? 0 : Number(n) || 0;
}

/**
 * @param {Object} options
 * @param {boolean} options.enabled
 * @param {Object|null} options.address - { postcode, stateCode, city?, countryCode? }
 * @param {Array<{productId?: string, productIds?: string[], quantity?: number}>} options.cartItems
 * @returns {import('@tanstack/react-query').UseQueryResult}
 */
export function usePdpShippingMethods({enabled = true, address = null, cartItems = []}) {
  const {site} = useMultiSite();
  const siteId = site?.id || 'kl-au';

  const postcode = address?.postcode != null ?
    String(address.postcode).trim() :
    address?.postalCode != null ?
      String(address.postalCode).trim() :
      '';
  const stateCode = String(address?.stateCode || address?.state || '').trim().toUpperCase();
  const countryCode = String(address?.countryCode || 'AU').trim() || 'AU';

  const cartKey = useMemo(
    () => (cartItems || [])
      .map((i) => `${(i.productIds || []).join(',')}:${i.quantity ?? 1}`)
      .join('|'),
    [cartItems]
  );

  const isSingapore = countryCode === 'SG';
  const effectiveStateCode = stateCode || (isSingapore ? 'SG' : '');
  const queryEnabled = Boolean(enabled && postcode && effectiveStateCode);

  return useQuery({
    queryKey: ['pdpShippingMethods', siteId, postcode, effectiveStateCode, countryCode, cartKey],
    queryFn: async ({queryKey}) => {
      const [, qSiteId, qPostcode, qStateCode, qCountryCode] = queryKey;

      const normalizedRequestAddress = {
        postcode: qPostcode,
        postalCode: qPostcode,
        stateCode: qStateCode,
        state: qStateCode,
        countryCode: qCountryCode || 'AU',
      };

      // Flatten cartItems into individual productItems for the server
      const productItems = [];

      for (const row of cartItems || []) {
        if (Array.isArray(row.productIds) && row.productIds.length > 0) {
          for (const id of row.productIds) {
            if (id) {
              productItems.push({productId: id, quantity: 1});
            }
          }
        } else if (row.productId) {
          productItems.push({productId: row.productId, quantity: row.quantity ?? 1});
        }
      }

      const serverBody = {
        siteId: qSiteId,
        address: {
          postcode: qPostcode,
          postalCode: qPostcode,
          city: address?.city ?? null,
          stateCode: qStateCode,
          state: qStateCode,
          countryCode: qCountryCode || 'AU',
        },
        productItems,
        cartItems,
        skipPriorityFilter: true,
      };

      let serverJson = null;

      try {
        const response = await fetch('/server/shipping/pdp-sfcc-estimate', {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify(serverBody),
        });

        if (response.ok) {
          serverJson = await response.json();
        }
      } catch {
        // Network/fetch failure — serverJson stays null, handled below.
      }

      const source = serverJson?.source ?? null;
      const serverMethods = Array.isArray(serverJson?.methods) ? serverJson.methods : [];

      return {
        success: serverMethods.length > 0 || serverJson?.success === true,
        source,
        methods: serverMethods,
        requestedDeliveryAddress: normalizedRequestAddress,
        timestamp: serverJson?.timestamp ?? new Date().toISOString(),
      };
    },
    enabled: queryEnabled,
    retry: 1,
    staleTime: 0,
    select: (data) => {
      const rawMethods = Array.isArray(data?.methods) ? data.methods : [];
      const treatAsFailure = data?.success !== true && rawMethods.length === 0;

      if (treatAsFailure) {
        return {...data, methods: []};
      }

      const methods = [...rawMethods].sort(
        (a, b) => methodPriceAmount(a) - methodPriceAmount(b)
      );

      return {...data, methods};
    },
  });
}

export default usePdpShippingMethods;
